// Conversation d'une demande déposée avec le service (messagerie du socle, H8-01, R15) : messages
// des deux côtés avec leurs pièces, nouveaux messages signalés, envoi avec les pièces déjà fournies
// à la demande (seules pièces que l'API accepte), états « pas encore ouverte » et « archivée » dits
// tels quels. Les notes internes du service ne sont jamais servies par l'API usager.
import { Bandeau, Bouton, CaseACocher, EtatVide, Formulaire, ZoneTexte, tailleLisible } from '@cc/ui';
import { useQueryClient } from '@tanstack/react-query';
import { MessageSquareDashed, Paperclip } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { messageErreur } from '../../../api/erreurs';
import { EtatRequete } from '../../../commun/EtatRequete';
import { dateHeure } from '../../../commun/format';
import { useAction } from '../../../commun/useAction';
import { CLES, envoyerMessage, ouvrirDocument, useConversation } from '../api';
import { LONGUEUR_MAX_MESSAGE, MAX_PIECES_MESSAGE, messageErreurEnvoi, nomAuteur, nouveauxMessages } from '../conversation';
import { useSessionUsager } from '../session/FournisseurSessionUsager';
import type { ConversationDossier, FicheDossierUsager, MessageConversationDossier } from '../types';
import styles from './suivi.module.css';
import commun from '../citoyen.module.css';

type Piece = MessageConversationDossier['pieces'][number];

function PieceJointe({ dossierId, piece, onErreur }: { dossierId: string; piece: Piece; onErreur: (message: string) => void }) {
  return (
    <li>
      <button
        type="button"
        className={styles.piece}
        onClick={() => ouvrirDocument(`/usager/dossiers/${dossierId}/documents/${piece.document_id}`).catch((e: unknown) => onErreur(messageErreur(e)))}
      >
        <Paperclip aria-hidden strokeWidth={1.75} width={14} height={14} />
        {piece.titre}
        <span className={styles.taillePiece}>{tailleLisible(piece.taille)}</span>
      </button>
    </li>
  );
}

function Messages({ d, c, nouveaux, moi }: { d: FicheDossierUsager; c: ConversationDossier; nouveaux: string[]; moi: string | null }) {
  const [erreur, setErreur] = useState<string | null>(null);
  if (c.messages.length === 0) {
    return <p className={commun.aide}>{c.statut === 'archivee' ? 'Aucun message n\'a été échangé.' : 'Aucun message pour l\'instant. Écrivez au service ci-dessous.'}</p>;
  }
  return (
    <>
      {erreur ? (
        <Bandeau type="erreur" onFermer={() => setErreur(null)}>
          {erreur}
        </Bandeau>
      ) : null}
      <ol className={styles.messages} aria-label="Messages">
        {c.messages.map((m) => (
          <li key={m.id} className={styles.message} data-cote={m.auteur.cote}>
            <span className={styles.auteur}>
              {nomAuteur(m, moi)} · {dateHeure(m.horodatage)}
              {nouveaux.includes(m.id) ? (
                <>
                  {' · '}
                  <strong className={styles.nouveau}>Nouveau</strong>
                </>
              ) : null}
            </span>
            <p className={styles.contenuMessage}>{m.contenu}</p>
            {m.pieces.length > 0 ? (
              <ul className={styles.pieces} aria-label="Pièces jointes">
                {m.pieces.map((p) => (
                  <PieceJointe key={p.document_id} dossierId={d.id} piece={p} onErreur={setErreur} />
                ))}
              </ul>
            ) : null}
          </li>
        ))}
      </ol>
    </>
  );
}

function NouveauMessage({ d }: { d: FicheDossierUsager }) {
  const [contenu, setContenu] = useState('');
  const [pieces, setPieces] = useState<string[]>([]);
  const envoi = useAction({
    executer: () => envoyerMessage(d.id, { contenu: contenu.trim(), pieces }),
    succes: 'Message envoyé au service.',
    invalider: [[...CLES.conversation(d.id)], [...CLES.dossier(d.id)]],
    apres: () => {
      setContenu('');
      setPieces([]);
    },
  });
  const basculer = (id: string, cochee: boolean) => setPieces((l) => (cochee ? [...l.filter((x) => x !== id), id] : l.filter((x) => x !== id)));
  return (
    <Formulaire
      libelle="Nouveau message"
      onSoumettre={() => {
        if (contenu.trim()) envoi.lancer(undefined);
      }}
      actions={
        <Bouton type="submit" chargement={envoi.enCours} libelleChargement="Envoi en cours" disabled={!contenu.trim()}>
          Envoyer
        </Bouton>
      }
    >
      {envoi.erreur ? <Bandeau type="erreur">{messageErreurEnvoi(envoi.erreur)}</Bandeau> : null}
      <ZoneTexte
        id="message-contenu"
        libelle="Votre message au service"
        value={contenu}
        onChange={(e) => setContenu(e.target.value)}
        rows={3}
        maxLength={LONGUEUR_MAX_MESSAGE}
        required
        aide="Lu par les agents du service qui traite votre demande. Une pièce nouvelle se dépose avec « Fournir le complément » quand le service le demande."
      />
      {d.pieces.length > 0 ? (
        <fieldset className={styles.choixPieces}>
          <legend className={styles.legende}>
            Joindre une pièce de la demande <span className={commun.aide}>(facultatif, {MAX_PIECES_MESSAGE} au plus)</span>
          </legend>
          {d.pieces.map((p) => (
            <CaseACocher
              key={p.document_id}
              libelle={p.titre}
              checked={pieces.includes(p.document_id)}
              disabled={envoi.enCours || (!pieces.includes(p.document_id) && pieces.length >= MAX_PIECES_MESSAGE)}
              onChange={(e) => basculer(p.document_id, e.target.checked)}
            />
          ))}
        </fieldset>
      ) : null}
    </Formulaire>
  );
}

/** Conversation d'une demande : lecture (qui marque lu), nouveaux messages signalés, envoi si elle est active. */
export function Conversation({ d }: { d: FicheDossierUsager }) {
  const client = useQueryClient();
  const { profil } = useSessionUsager();
  const moi = profil?.id ?? null;
  const conversation = useConversation(d.id);
  const [nouveaux, setNouveaux] = useState<string[]>([]);
  // Non-lus connus par la fiche à l'arrivée sur l'écran : ils restent signalés même si le serveur les a déjà marqués lus.
  const nonLusAvant = useRef(d.conversation.non_lus);
  // À chaque lecture, les messages nouveaux sont signalés et la fiche en cache passe à zéro non-lu.
  useEffect(() => {
    const c = conversation.data;
    if (!c) return;
    const ids = nouveauxMessages(c, moi, nonLusAvant.current);
    if (ids.length > 0) setNouveaux((n) => [...new Set([...n, ...ids])]);
    if (c.non_lus > 0 || nonLusAvant.current > 0) {
      nonLusAvant.current = 0;
      client.setQueryData<FicheDossierUsager>(CLES.dossier(d.id), (f) => (f && f.conversation.non_lus > 0 ? { ...f, conversation: { ...f.conversation, non_lus: 0 } } : f));
    }
  }, [conversation.data, moi, client, d.id]);

  if (conversation.error) {
    return (
      <EtatRequete chargement={false} erreur={conversation.error} onReessayer={() => void conversation.refetch()} fonction="La conversation">
        {null}
      </EtatRequete>
    );
  }
  const c = conversation.data;
  if (!c) {
    return (
      <EtatRequete chargement erreur={null} lignes={4}>
        {null}
      </EtatRequete>
    );
  }
  if (c.statut === 'aucune') {
    return (
      <EtatVide titre="Aucune conversation ouverte pour l'instant" icone={MessageSquareDashed} niveau={2}>
        <p>
          La conversation s&apos;ouvre au dépôt en ligne d&apos;une demande. Pour une demande enregistrée au guichet, le service l&apos;ouvre en vous écrivant le premier ; vous
          pourrez alors lui répondre ici. Toute demande de complément apparaît sur la fiche de suivi.
        </p>
      </EtatVide>
    );
  }
  return (
    <div className={commun.pile}>
      {c.statut === 'archivee' ? (
        <Bandeau type="info" titre="Conversation archivée">
          La demande est terminée : les messages restent lisibles, plus aucun ne peut être envoyé.
        </Bandeau>
      ) : null}
      <Messages d={d} c={c} nouveaux={nouveaux} moi={moi} />
      {c.statut === 'active' ? <NouveauMessage d={d} /> : null}
    </div>
  );
}
